import React from 'react';
import { Send, Clock, Shield, Check, Sparkles } from 'lucide-react';

export default function Landing({ onGetStarted }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900">MeetingPro</h1>
          </div>
          <button
            onClick={onGetStarted}
            className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base"
          >
            Login
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            AI-Powered Meeting Follow-ups
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Transform Meeting Notes into Professional Follow-ups in{' '}
            <span className="text-blue-600">30 Seconds</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Stop spending hours writing meeting follow-ups. Paste your notes, get a polished email with clear action items, owners, and deadlines—instantly.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={onGetStarted}
              className="px-8 py-4 bg-blue-600 text-white rounded-lg text-lg font-semibold hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-5 h-5" />
              Get Started Free
            </button>
            <button
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-gray-700 rounded-lg text-lg font-semibold hover:bg-gray-50 border-2 border-gray-200"
            >
              See How It Works
            </button>
          </div>
          
          <p className="text-sm text-gray-500">
            ✓ No credit card required • ✓ 5 free meetings • ✓ Takes 30 seconds
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="bg-white py-16 sm:py-20 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              The Problem with Meeting Follow-ups
            </h2>
            <p className="text-lg text-gray-600">
              You've had a productive meeting. Everyone agreed on next steps. But now you need to:
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">😫</div>
              <h3 className="font-semibold text-gray-900 mb-2">Spend 30+ minutes</h3>
              <p className="text-sm text-gray-600">Turning messy notes into a clear email</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">😰</div>
              <h3 className="font-semibold text-gray-900 mb-2">Worry about clarity</h3>
              <p className="text-sm text-gray-600">Did you assign owners? Set deadlines?</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">🤔</div>
              <h3 className="font-semibold text-gray-900 mb-2">Second-guess yourself</h3>
              <p className="text-sm text-gray-600">Is this email professional enough?</p>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              MeetingPro does all this in <span className="text-blue-600">30 seconds</span>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Three simple steps to perfect meeting follow-ups
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Paste Your Notes</h3>
              <p className="text-gray-600">
                Copy your messy meeting notes—bullet points, scribbles, whatever you have.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Generates Follow-up</h3>
              <p className="text-gray-600">
                Claude AI creates a professional email with clear action items, owners, and deadlines.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Copy & Send</h3>
              <p className="text-gray-600">
                One-click copy. Paste into your email. Done. Your team knows exactly what to do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Clock className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">30-Second Generation</h3>
              <p className="text-gray-600 text-sm">Get professional follow-ups faster than you can write a single paragraph.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Check className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear Action Items</h3>
              <p className="text-gray-600 text-sm">Every task has an owner and deadline. No ambiguity.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Sparkles className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Multiple Tones</h3>
              <p className="text-gray-600 text-sm">Regenerate in Executive, Client-Friendly, Internal, or Risk-Aware tones.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Shield className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Data is Private</h3>
              <p className="text-gray-600 text-sm">We don't sell your data or use it to train AI models. Encrypted and secure.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Send className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">One-Click Copy</h3>
              <p className="text-gray-600 text-sm">Copy the entire email to your clipboard instantly. Paste and send.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Clock className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Save All Meetings</h3>
              <p className="text-gray-600 text-sm">Access your past meeting follow-ups anytime, from any device.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600">
              Start free. Upgrade when you need more.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600">/forever</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>10,000 tokens</strong> (~5 meetings)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">All core features</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Save meetings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Access from any device</span>
                </li>
              </ul>
              <button
                onClick={onGetStarted}
                className="w-full py-3 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200"
              >
                Get Started Free
              </button>
            </div>
            
            {/* Pro Plan */}
            <div className="bg-blue-600 text-white rounded-lg p-8 relative">
              <div className="absolute top-4 right-4 bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-xs font-bold">
                BEST VALUE
              </div>
              <h3 className="text-2xl font-bold mb-2">Pro</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">$12</span>
                <span className="text-blue-100">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span><strong>50,000 tokens</strong> (~25 meetings)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span>Everything in Free</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span>Tokens reset monthly</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span>Cancel anytime</span>
                </li>
              </ul>
              <button
                onClick={onGetStarted}
                className="w-full py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50"
              >
                Start Pro Trial
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Save Hours on Meeting Follow-ups?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join professionals who've already transformed their meeting workflow.
          </p>
          <button
            onClick={onGetStarted}
            className="px-8 py-4 bg-white text-blue-600 rounded-lg text-lg font-semibold hover:bg-blue-50 shadow-xl inline-flex items-center gap-2"
          >
            <Send className="w-5 h-5" />
            Get Started Free
          </button>
          <p className="text-blue-100 text-sm mt-4">
            No credit card required • Takes 30 seconds to set up
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              © 2026 MeetingPro. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="mailto:support@meetingpro.app" className="text-gray-600 hover:text-blue-600">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}